import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Divider, Button, Checkbox, Popup } from 'semantic-ui-react'

import Waypoints from './Waypoints'

import { ProfilePicker } from 'components/profile-picker'
import { SettingsButton } from 'components/SettingsButton'
import { SettingsFooter } from 'components/SettingsFooter'
import { Settings } from './settings'
import { DateTimePicker } from 'components/DateTimePicker'

export const DRIVEPOINTS_BACKEND_URL =
  process.env.REACT_APP_DRIVEPOINTS_BACKEND_URL

import {
  doAddWaypoint,
  doRemoveWaypoint,
  makeRequest,
  clearRoutes,
  optimizeRoute,
  refreshTrafficForCurrentRoutes,
} from 'actions/directionsActions'
import {
  updateProfile,
  doShowSettings,
  updatePermalink,
  resetSettings,
  doUpdateDateTime,
  doUpdateTrafficEnabled,
  sendMessage,
} from 'actions/commonActions'
import { isTrafficProfile } from 'traffic'
import { withAuth } from 'utils/withAuth'

class DirectionsControl extends React.Component {
  static propTypes = {
    profile: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    optimizing: PropTypes.bool,
    dateTime: PropTypes.shape({
      type: PropTypes.number,
      value: PropTypes.string,
    }),
    trafficEnabled: PropTypes.bool,
    auth: PropTypes.object,
    directions: PropTypes.object,
    settings: PropTypes.object,
  }

  constructor(props) {
    super(props)
    this.state = {
      returnToStart: false,
    }
  }

  handleUpdateProfile = (event, data) => {
    const { dispatch } = this.props
    dispatch(updateProfile({ profile: data.valhalla_profile }))
    dispatch(resetSettings())
    dispatch(updatePermalink())
  }

  handleAddWaypoint = (event, data) => {
    const { dispatch } = this.props
    dispatch(doAddWaypoint())
  }

  handleRemoveWaypoints = () => {
    const { dispatch } = this.props
    dispatch(doRemoveWaypoint({ auth: this.props.auth }))
    dispatch(clearRoutes())
  }

  componentDidUpdate(prevProps) {
    const { dispatch, profile, dateTime } = this.props

    // Single place for route request: only when profile or dateTime changes (one request per change)
    const profileChanged = prevProps.profile !== profile
    const dateTimeChanged =
      prevProps.dateTime?.type !== dateTime?.type ||
      prevProps.dateTime?.value !== dateTime?.value
    if (profileChanged || dateTimeChanged) {
      dispatch(makeRequest({ auth: this.props.auth }))
    }

    // Check if locations were added that exceed the limit
    const count = this.getActiveWaypointsCount()
    const prevCount = this.getActiveWaypointsCountFromProps(prevProps)
    const { returnToStart } = this.state

    if (count > prevCount) {
      const maxLocations = returnToStart ? 19 : 20

      if (count >= maxLocations) {
        dispatch(
          sendMessage({
            type: 'warning',
            icon: 'warning',
            description: returnToStart
              ? 'You need less than 19 locations when return to start is enabled'
              : 'You need less than 20 locations',
            title: 'Too Many Locations',
          })
        )
      }
    }
  }

  // TODO: remove this code as it has been migrated to componentDidUpdate
  // UNSAFE_componentWillReceiveProps(nextProps) {
  //   const { dispatch } = this.props
  //   if (this.props.profile !== nextProps.profile) {
  //     dispatch(makeRequest())
  //   }
  // }

  handleSettings = () => {
    const { dispatch } = this.props
    dispatch(doShowSettings())
  }

  handleDateTime = (type, value) => {
    const { dispatch } = this.props
    dispatch(doUpdateDateTime(type, value))
  }

  handleTrafficChange = (enabled) => {
    const { dispatch } = this.props
    dispatch(doUpdateTrafficEnabled(enabled))
    if (enabled) {
      if (!process.env.REACT_APP_MAPBOX_ACCESS_TOKEN) {
        dispatch(
          sendMessage({
            type: 'warning',
            icon: 'warning',
            title: 'Traffic unavailable',
            description: 'REACT_APP_MAPBOX_ACCESS_TOKEN is not set',
          })
        )
        return
      }
      dispatch(refreshTrafficForCurrentRoutes())
    } else {
      dispatch(refreshTrafficForCurrentRoutes())
    }
  }

  handleReturnToStartChange = (event, data) => {
    const { dispatch } = this.props
    const newReturnToStart = data.checked
    const count = this.getActiveWaypointsCount()

    if (newReturnToStart && count >= 19) {
      dispatch(
        sendMessage({
          type: 'warning',
          icon: 'warning',
          description:
            'You need less than 19 locations when return to start is enabled',
          title: 'Too Many Locations',
        })
      )
      return
    }

    this.setState({ returnToStart: newReturnToStart })
  }

  getActiveWaypointsCountFromProps = (props) => {
    const { directions } = props
    const { waypoints } = directions
    let count = 0
    for (const waypoint of waypoints) {
      if (waypoint.geocodeResults.length > 0) {
        for (const result of waypoint.geocodeResults) {
          if (result.selected) {
            count++
            break
          }
        }
      }
    }
    return count
  }

  handleOptimize = () => {
    const { dispatch, auth } = this.props
    dispatch(optimizeRoute({ auth, returnToStart: this.state.returnToStart }))
  }

  getActiveWaypointsCount = () => {
    const { directions } = this.props
    const { waypoints } = directions
    let count = 0
    for (const waypoint of waypoints) {
      if (waypoint.geocodeResults.length > 0) {
        for (const result of waypoint.geocodeResults) {
          if (result.selected) {
            count++
            break
          }
        }
      }
    }
    return count
  }

  shouldShowOptimizeButton = () => {
    const count = this.getActiveWaypointsCount()
    return count > 2 && count < 20
  }

  isOptimizeButtonDisabled = () => {
    const count = this.getActiveWaypointsCount()
    const { returnToStart } = this.state
    const maxLocations = returnToStart ? 19 : 20
    return count >= maxLocations
  }

  getOptimizeButtonTooltip = () => {
    const count = this.getActiveWaypointsCount()
    const { returnToStart } = this.state
    const maxLocations = returnToStart ? 19 : 20
    if (count >= maxLocations) {
      return returnToStart
        ? 'You need less than 19 locations when return to start is enabled'
        : 'You need less than 20 locations'
    }
    return 'Optimize route order'
  }

  shouldComponentUpdate(nextProps) {
    const { dateTime, profile, directions, optimizing, trafficEnabled } =
      this.props
    const waypointsChanged =
      directions.waypoints.length !== nextProps.directions.waypoints.length ||
      directions.waypoints !== nextProps.directions.waypoints ||
      this.getActiveWaypointsCount() !==
        this.getActiveWaypointsCountFromProps(nextProps)
    return (
      dateTime.type !== nextProps.dateTime.type ||
      dateTime.value !== nextProps.dateTime.value ||
      profile !== nextProps.profile ||
      optimizing !== nextProps.optimizing ||
      trafficEnabled !== nextProps.trafficEnabled ||
      waypointsChanged
    )
  }

  getProfiles = () => {
    if (DRIVEPOINTS_BACKEND_URL) {
      return ['autodp', 'truckdp', 'car', 'truck']
    }
    return [
      'bicycle',
      'pedestrian',
      'car',
      'truck',
      'bus',
      'motor_scooter',
      'motorcycle',
    ]
  }

  getPopupContent = () => {
    if (DRIVEPOINTS_BACKEND_URL) {
      return ['Drivepoints Car', 'Drivepoints Truck', 'Car', 'Truck']
    }
    return [
      'Bicycle',
      'Pedestrian',
      'Car',
      'Truck',
      'Bus',
      'Motor Scooter',
      'Motorcycle',
    ]
  }

  render() {
    const { profile, loading, optimizing, dateTime, trafficEnabled } =
      this.props
    const showOptimize = this.shouldShowOptimizeButton()
    const isOptimizeDisabled = this.isOptimizeButtonDisabled()
    const trafficAvailable =
      isTrafficProfile(profile) && !!process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
    return (
      <React.Fragment>
        <div className="flex flex-column content-between">
          <div>
            <div className="pa2 flex flex-row justify-between">
              <ProfilePicker
                group={'directions'}
                profiles={this.getProfiles()}
                loading={loading}
                popupContent={this.getPopupContent()}
                activeProfile={profile}
                handleUpdateProfile={this.handleUpdateProfile}
              />
              <div className="flex flex-row">
                {showOptimize && (
                  <Popup
                    content={this.getOptimizeButtonTooltip()}
                    size={'tiny'}
                    trigger={
                      <Button
                        basic
                        size="tiny"
                        disabled={isOptimizeDisabled || optimizing}
                        loading={optimizing}
                        onClick={this.handleOptimize}
                        style={{ marginRight: '4px' }}
                      >
                        Optimize
                      </Button>
                    }
                  />
                )}
                <SettingsButton handleSettings={this.handleSettings} />
              </div>
            </div>
            {showOptimize && (
              <div className="pa2">
                <Checkbox
                  label="Return to start point"
                  checked={this.state.returnToStart}
                  onChange={this.handleReturnToStartChange}
                  style={{ display: 'none' }}
                />
              </div>
            )}
            <div className="flex flex-wrap justify-between">
              <Waypoints />
            </div>
            <div className="pa2 flex flex-wrap justify-between">
              <Settings
                handleAddWaypoint={this.handleAddWaypoint}
                handleRemoveWaypoints={this.handleRemoveWaypoints}
              />
            </div>
            <DateTimePicker
              type={dateTime.type}
              value={dateTime.value}
              onChange={this.handleDateTime}
              trafficEnabled={trafficEnabled && trafficAvailable}
              trafficAvailable={trafficAvailable}
              onTrafficChange={this.handleTrafficChange}
            />
          </div>
          <Divider fitted />
          <SettingsFooter />
        </div>
      </React.Fragment>
    )
  }
}

const mapStateToProps = (state) => {
  const { profile, loading, optimizing, dateTime, settings, trafficEnabled } =
    state.common
  const { directions } = state
  return {
    profile,
    loading,
    optimizing,
    dateTime,
    trafficEnabled,
    directions,
    settings,
  }
}

export default connect(mapStateToProps)(withAuth(DirectionsControl))
